import { ChevronRight, FileText, Folder, type LucideIcon } from "lucide-react";
import type {
  BranchChange,
  BranchChangePreview,
} from "playground/branches/changes";
import type { TaskReview } from "playground/migrations/contract";
import type { ComponentProps, ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Empty, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { cn } from "@/lib/utils";

export function ChangeRow({
  icon: Icon,
  title,
  description,
  badge,
  children,
  ...props
}: {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  badge: ReactNode;
  children: ReactNode;
} & Pick<ComponentProps<typeof Collapsible>, "open" | "onOpenChange">) {
  return (
    <Collapsible {...props}>
      <CollapsibleTrigger asChild>
        <Item asChild>
          <button
            type="button"
            className="group w-full flex-nowrap text-left hover:bg-accent/50"
          >
            <ItemMedia variant="icon">
              <Icon />
            </ItemMedia>
            <ItemContent className="min-w-0">
              <ItemTitle className="max-w-full break-words">{title}</ItemTitle>
              <ItemDescription className="break-words">
                {description}
              </ItemDescription>
            </ItemContent>
            <ItemActions className="shrink-0">
              {badge}
              <ChevronRight
                className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
                aria-hidden
              />
            </ItemActions>
          </button>
        </Item>
      </CollapsibleTrigger>
      <CollapsibleContent className="min-w-0 px-4 pb-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

interface PreviewProps {
  previewPath: string | null;
  preview: BranchChangePreview | null;
  previewError: string | null;
  onPreview: (path: string | null) => void;
}

function changeTitle(change: BranchChange): string {
  const name = change.path.split("/").at(-1);
  switch (change.kind) {
    case "added":
      return `Add ${name}`;
    case "removed":
      return `Remove ${name}`;
    case "modified":
      return `Update ${name}`;
  }
}

function FileChange({
  change,
  previewPath,
  preview,
  previewError,
  onPreview,
}: { change: BranchChange } & PreviewProps) {
  return (
    <ChangeRow
      icon={change.mimeType === "inode/directory" ? Folder : FileText}
      title={changeTitle(change)}
      description={change.path}
      badge={
        change.diverged ? <Badge variant="outline">Main changed</Badge> : null
      }
      open={previewPath === change.path}
      onOpenChange={(open) => onPreview(open ? change.path : null)}
    >
      {change.diverged && (
        <p className="mb-3 text-sm text-muted-foreground">
          This file also changed in the main project. Compare both versions
          before applying.
        </p>
      )}
      {preview?.path === change.path && preview.annotations > 0 && (
        <Badge variant="outline" className="mb-3">
          {preview.annotations === 1
            ? "1 annotation changed"
            : `${preview.annotations} annotations changed`}
        </Badge>
      )}
      {change.mimeType === "inode/directory" ? (
        <p className="text-sm text-muted-foreground">
          This changes the project folder. Its file changes are listed
          separately.
        </p>
      ) : previewError ? (
        <p role="alert" className="text-sm text-destructive">
          {previewError}
        </p>
      ) : !preview || preview.path !== change.path ? (
        <p role="status" className="text-sm text-muted-foreground">
          Loading diff…
        </p>
      ) : preview.patch ? (
        <pre className="max-h-[50vh] overflow-auto rounded-md bg-muted/40 p-3 font-mono text-[11px] leading-5">
          {preview.patch.split("\n").map((line, index) => (
            <div
              key={index}
              className={cn(
                line.startsWith("+") &&
                  !line.startsWith("+++") &&
                  "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300",
                line.startsWith("-") &&
                  !line.startsWith("---") &&
                  "bg-red-500/15 text-red-800 dark:text-red-300",
              )}
            >
              {line || " "}
            </div>
          ))}
        </pre>
      ) : preview.annotations > 0 && !preview.truncated ? null : (
        <p className="text-sm text-muted-foreground">
          {preview.truncated
            ? "Text is too large or complex for a diff preview."
            : "A text diff is unavailable for this file."}
        </p>
      )}
      {change.diverged &&
        preview?.path === change.path &&
        preview.mainText !== null && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer">Current main text</summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono">
              {preview.mainText}
            </pre>
          </details>
        )}
    </ChangeRow>
  );
}

export function TaskChanges({
  review,
  children,
  ...preview
}: { review: TaskReview; children: ReactNode } & PreviewProps) {
  const supporting = review.changes.filter(
    (change) =>
      change.mimeType === "inode/directory" ||
      (review.migration &&
        Object.hasOwn(review.migration.sources, change.path)),
  );
  const files = review.changes.filter((change) => !supporting.includes(change));
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <ItemGroup className="divide-y">
        {files.map((change) => (
          <FileChange key={change.path} change={change} {...preview} />
        ))}
        {children}
      </ItemGroup>
      {supporting.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-md px-4 py-3 text-left text-xs text-muted-foreground hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronRight
              className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
              aria-hidden
            />
            Supporting files ({supporting.length})
            {supporting.some((change) => change.diverged) && (
              <Badge variant="outline">Main changed</Badge>
            )}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ItemGroup className="divide-y">
              {supporting.map((change) => (
                <FileChange key={change.path} change={change} {...preview} />
              ))}
            </ItemGroup>
          </CollapsibleContent>
        </Collapsible>
      )}
      {!review.changes.length && !children && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No changes to apply</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}
    </div>
  );
}
